import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MessageSquare, Send, Sparkles, Bot, User as UserIcon } from 'lucide-react';
import { Card } from '../components/ui/card';
import { useAuth, API } from '../context/AuthContext';

export default function ChatPage() {
  const { user, login, loading: authLoading } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [sessionId] = useState(() => `chat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const suggestions = [
    'Newton ର ପ୍ରଥମ ନିୟମ କ\'ଣ?',
    'Photosynthesis ର ପ୍ରକ୍ରିୟା ବୁଝାନ୍ତୁ',
    'ବର୍ଗ ଓ ଆୟତର ମଧ୍ୟରେ ପାର୍ଥକ୍ୟ?',
    'JNVST ପାଇଁ କେମିତି ପ୍ରସ୍ତୁତି କରିବି?',
  ];

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg) return;
    if (!user) { login(); return; }
    setMessages((m) => [...m, { role: 'user', content: msg }, { role: 'assistant', content: '' }]);
    setInput('');
    setStreaming(true);

    try {
      const res = await fetch(`${API}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      if (!res.ok) {
        const detail = await res.json().then(j => j?.detail).catch(() => null);
        throw new Error(typeof detail === 'string' ? detail : 'Chat failed');
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let acc = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        acc += dec.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: 'assistant', content: acc };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: 'assistant', content: e?.message && e.message !== 'Chat failed' ? `⚠️ ${e.message}` : '⚠️ ଉତ୍ତର ଆଣିବାରେ ତ୍ରୁଟି। ପୁଣି ଚେଷ୍ଟା କରନ୍ତୁ।' };
        return copy;
      });
    } finally { setStreaming(false); }
  };

  return (
    <div className="flex flex-col h-screen">
      <div className="px-4 pt-4 pb-3 space-y-3 shrink-0 bg-white border-b border-gray-100">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-emerald-600 font-medium">
          <ArrowLeft size={16} /> Home
        </Link>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-fuchsia-500 to-pink-600 text-white flex items-center justify-center">
            <Bot size={20} />
          </div>
          <div>
            <div className="font-extrabold text-base text-gray-900">Guru AI ସହାୟକ</div>
            <div className="text-[10px] text-emerald-600 font-semibold flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Online · Bilingual (Odia + English)
            </div>
          </div>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" style={{ paddingBottom: '120px' }}>
        {messages.length === 0 && (
          <>
            {!user && !authLoading && (
              <Card className="rounded-2xl p-3 border-amber-200 bg-amber-50 flex items-center justify-between gap-2" data-testid="chat-login-notice">
                <p className="text-[11px] text-amber-800 font-semibold">AI ସହାୟକ ବ୍ୟବହାର ପାଇଁ ଲଗଇନ୍ ଦରକାର — ଲଗଇନ୍ ପରେ ଆପଣ ଏହି ପୃଷ୍ଠାକୁ ଫେରିବେ।</p>
                <button onClick={login} className="text-[11px] font-bold text-white bg-emerald-600 px-3 py-1.5 rounded-lg shrink-0" data-testid="chat-login-button">ଲଗଇନ୍</button>
              </Card>
            )}
            <Card className="rounded-2xl p-4 border-purple-200 bg-purple-50">
              <Sparkles size={18} className="text-purple-600" />
              <div className="font-bold text-sm text-purple-900 mt-1">ସ୍ୱାଗତ! ଯେକୌଣସି ପ୍ରଶ୍ନ ପଚାରନ୍ତୁ</div>
              <p className="text-xs text-purple-700 mt-1">ଗଣିତ, ବିଜ୍ଞାନ, ଓଡ଼ିଆ, ଇଂରାଜୀ ଆଦିରେ ସନ୍ଦେହ ଦୂର କରନ୍ତୁ।</p>
            </Card>
            <div className="space-y-2">
              {suggestions.map((s) => (
                <button key={s} onClick={() => send(s)} className="w-full text-left p-3 rounded-xl bg-white border border-gray-200 text-sm text-gray-700 hover:border-purple-300 hover:bg-purple-50 transition-colors">
                  💡 {s}
                </button>
              ))}
            </div>
          </>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${m.role === 'user' ? 'bg-emerald-500' : 'bg-gradient-to-br from-fuchsia-500 to-pink-600'} text-white`}>
              {m.role === 'user' ? <UserIcon size={15} /> : <Bot size={15} />}
            </div>
            <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${m.role === 'user' ? 'bg-emerald-500 text-white' : 'bg-white border border-gray-200 text-gray-800'}`}>
              {m.content || (streaming && i === messages.length - 1 ? <span className="inline-block w-2 h-4 bg-gray-400 animate-pulse" /> : '')}
            </div>
          </div>
        ))}
      </div>

      <div className="fixed bottom-16 left-1/2 -translate-x-1/2 w-full max-w-md bg-white border-t border-gray-100 p-3 z-20">
        <div className="flex gap-2 items-end">
          <textarea data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="ଆପଣଙ୍କ ପ୍ରଶ୍ନ ଲେଖନ୍ତୁ..."
            rows={1}
            className="flex-1 border border-gray-200 rounded-2xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:border-purple-400 max-h-24"
          />
          <button onClick={() => send()} disabled={streaming || !input.trim()} data-testid="chat-send-button"
            className="w-11 h-11 rounded-2xl bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white flex items-center justify-center shrink-0">
            <Send size={17} />
          </button>
        </div>
      </div>
    </div>
  );
}
